import { UploadFile } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { useCallback, useMemo } from 'react';

interface UseIndexedDbUploadDraftOptions {
  storageKey?: string;
  entry: string;
}

interface UseIndexedDbUploadDraftReturn {
  persistFiles: (files?: UploadFile[]) => Promise<void>;
  restoreFiles: () => Promise<UploadFile[]>;
  clearFiles: () => Promise<void>;
}

interface StoredUploadFile {
  uid: string;
  name: string;
  type: string;
  lastModified: number;
  blob: Blob;
}

interface StoredUploadDraftRecord {
  key: string;
  updatedAt: string;
  files: StoredUploadFile[];
}

// On utilise IndexedDB (et pas localStorage) pour persister des blobs/fichiers
// entre deux ouvertures de formulaire sans limite de taille trop contraignante.
const DB_NAME = 'hellio-upload-drafts';
const DB_VERSION = 1;
const STORE_NAME = 'draft-files';

function isIndexedDbSupported() {
  return typeof window !== 'undefined' && !!window.indexedDB;
}

function openDraftDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    // Création du store lors de la première ouverture (ou migration de version).
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readRecord(
  key: string,
): Promise<StoredUploadDraftRecord | undefined> {
  const database = await openDraftDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onsuccess = () => {
      resolve(request.result as StoredUploadDraftRecord | undefined);
    };
    request.onerror = () => reject(request.error);
    // On ferme explicitement la DB à la fin de chaque transaction pour éviter
    // de garder des connexions ouvertes inutilement.
    transaction.oncomplete = () => {
      database.close();
    };
    transaction.onerror = () => {
      database.close();
    };
    transaction.onabort = () => {
      database.close();
    };
  });
}

async function writeRecord(record: StoredUploadDraftRecord): Promise<void> {
  const database = await openDraftDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    store.put(record);

    transaction.oncomplete = () => {
      database.close();
      resolve();
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error);
    };
    transaction.onabort = () => {
      database.close();
      reject(transaction.error);
    };
  });
}

async function deleteRecord(key: string): Promise<void> {
  const database = await openDraftDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    store.delete(key);

    transaction.oncomplete = () => {
      database.close();
      resolve();
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error);
    };
    transaction.onabort = () => {
      database.close();
      reject(transaction.error);
    };
  });
}

function toStoredUploadFiles(files?: UploadFile[]): StoredUploadFile[] {
  if (!files?.length) {
    return [];
  }

  return files.reduce<StoredUploadFile[]>((storedFiles, file) => {
    const originFile = file.originFileObj;
    // Certains items Upload peuvent ne pas embarquer de File natif:
    // on les ignore pour ne persister que des données restaurables.
    if (!(originFile instanceof File)) {
      return storedFiles;
    }

    storedFiles.push({
      uid: file.uid,
      name: file.name || originFile.name,
      type: originFile.type || file.type || '',
      lastModified: originFile.lastModified,
      blob: originFile,
    });

    return storedFiles;
  }, []);
}

function toUploadFiles(files: StoredUploadFile[]): UploadFile[] {
  return files.map((file) => {
    // Recréation d'un File depuis le blob stocké pour réinjecter une structure
    // compatible avec le composant Upload d'Ant Design.
    const originFile = new File([file.blob], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    }) as RcFile;
    originFile.uid = file.uid;

    return {
      uid: file.uid,
      name: file.name,
      size: originFile.size,
      type: originFile.type,
      lastModifiedDate: new Date(file.lastModified),
      status: 'done',
      originFileObj: originFile,
    };
  });
}

export default function useIndexedDbUploadDraft({
  storageKey,
  entry,
}: UseIndexedDbUploadDraftOptions): UseIndexedDbUploadDraftReturn {
  // Clé finale: contexte du draft + entrée fonctionnelle (ex: beneficiary-files).
  const scopedStorageKey = useMemo(() => {
    if (!storageKey) {
      return undefined;
    }
    return `${storageKey}:${entry}`;
  }, [entry, storageKey]);

  const clearFiles = useCallback(async () => {
    if (!scopedStorageKey || !isIndexedDbSupported()) {
      return;
    }

    try {
      await deleteRecord(scopedStorageKey);
    } catch (error) {
      // no-op: ne bloque pas l'UI si IndexedDB est indisponible
      // (mode privé, quota, permissions).
    }
  }, [scopedStorageKey]);

  const persistFiles = useCallback(
    async (files?: UploadFile[]) => {
      if (!scopedStorageKey || !isIndexedDbSupported()) {
        return;
      }

      const storedFiles = toStoredUploadFiles(files);
      if (!storedFiles.length) {
        await clearFiles();
        return;
      }

      try {
        await writeRecord({
          key: scopedStorageKey,
          updatedAt: new Date().toISOString(),
          files: storedFiles,
        });
      } catch (error) {
        // no-op: la persistance est best-effort, la saisie doit rester utilisable.
      }
    },
    [clearFiles, scopedStorageKey],
  );

  const restoreFiles = useCallback(async () => {
    if (!scopedStorageKey || !isIndexedDbSupported()) {
      return [];
    }

    try {
      const record = await readRecord(scopedStorageKey);
      if (!record?.files?.length) {
        return [];
      }
      return toUploadFiles(record.files);
    } catch (error) {
      return [];
    }
  }, [scopedStorageKey]);

  return {
    persistFiles,
    restoreFiles,
    clearFiles,
  };
}
